import { LabelWithHelp } from "@components/common/label-with-help";
import { createTeamOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { SingleSelectAutocomplete } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { ALREADY_IN_TEAM_MESSAGE, TEAM_DISPLAY_NAME, TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import { LoadErrorAlert } from "@components/teams/add-to-team/load-error-alert";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector, teamsAcceptingMembersSelector } from "@store/teams/teams-selector";
import { fetchTeams } from "@store/teams/teams-slice-thunk";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { formatUserRoleType } from "@utils/data-display";
/** Kept as a constant so that a picker without disabled teams keeps the same reference */
const NO_TEAM_IDS = new Set();

/**
 * Creates the option of a team, adding its workspace role to the number of its members. A team
 * that cannot be picked says why instead, since the single select shows no message of its own.
 */
function createOption(team, isDisabled) {
  const option = createTeamOption({
    team
  });
  return {
    ...option,
    subtitle: isDisabled ? ALREADY_IN_TEAM_MESSAGE : `${option.subtitle} · ${formatUserRoleType(team.role)}`
  };
}

/**
 * Lets the user pick the team that members are added to.
 *
 * Each option shows the workspace role of the team next to its size, since joining a team gives
 * its members that role.
 *
 * The teams are loaded with the members of the workspace, so the picker waits until they are and
 * offers to load them again if that failed, instead of presenting an empty list as complete.
 */
export function TeamSelect({
  selectedTeamId,
  onTeamChange,
  isDisabled = false,
  disabledTeamIds = NO_TEAM_IDS
}) {
  const dispatch = useAppDispatch();
  const teams = useAppSelector(teamsAcceptingMembersSelector);
  // Other team requests share the status, so only the first load of the list tells it is complete
  const {
    status,
    hasFetchedTeams
  } = useAppSelector(fetchingTeamsFlagsSelector);
  const hasFailedToLoadTeams = !hasFetchedTeams && status === "rejected";
  const isLoadingTeams = !hasFetchedTeams && !hasFailedToLoadTeams;
  const options = teams.map(team => createOption(team, disabledTeamIds.has(team.id)));
  const selectedOption = options.find(option => option.value === selectedTeamId) ?? null;
  return <Stack sx={{
    gap: 1
  }}>
      {hasFailedToLoadTeams && <LoadErrorAlert title={`Couldn't load the ${TEAMS_DISPLAY_NAME} of the workspace.`} onRetry={() => void dispatch(fetchTeams({}))} />}

      <Stack>
        <LabelWithHelp title={capitalizeFirstLetter(TEAM_DISPLAY_NAME)} isRequired />
        <SingleSelectAutocomplete dataTestId="team-select" options={options} value={selectedOption} onChange={handleChange} isDisabled={isDisabled || isLoadingTeams || hasFailedToLoadTeams} getOptionDisabled={option_0 => disabledTeamIds.has(option_0.value)} placeholder={isLoadingTeams ? `Loading ${TEAMS_DISPLAY_NAME}…` : `Search or select a ${TEAM_DISPLAY_NAME}`} noOptionsText={teams.length ? `No matching ${TEAM_DISPLAY_NAME}` : `No ${TEAM_DISPLAY_NAME} to add members to. Create one on the Teams tab.`} sx={{
        width: "100%"
      }} />
      </Stack>
    </Stack>;

  /** Notifies the parent about the team behind the selected option */
  function handleChange(value) {
    const teamId = value && typeof value !== "string" ? value.value : undefined;
    onTeamChange(teams.find(team_0 => team_0.id === teamId));
  }
}